import React, { useState, useEffect } from 'react';
import { Card, Avatar, Typography, Button, Divider, Form, Input, message, Row, Col, Empty, Input as AntInput, Upload, Modal } from 'antd';
import { 
  EditOutlined, 
  SaveOutlined, 
  CloseOutlined, 
  MailOutlined, 
  CalendarOutlined,
  UserOutlined,
  GithubOutlined,
  TwitterOutlined,
  LinkOutlined,
  FileTextOutlined,
  HeartOutlined,
  CodeOutlined,
  EyeOutlined,
  UploadOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './index.css';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const getBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
};

const About = ({ user, onUpdateUser, posts = [] }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [fileList, setFileList] = useState([]);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  // 初始化表单数据
  useEffect(() => {
    if (user) {
      const initialValues = {
        bio: user.bio || '这个人很神秘，什么也没留下。',
        techStack: Array.isArray(user.techStack) ? user.techStack.join(', ') : user.techStack || '',
        interests: Array.isArray(user.interests) ? user.interests.join(', ') : user.interests || '',
        github: user.github || '',
        twitter: user.twitter || '',
        website: user.website || ''
      };
      form.setFieldsValue(initialValues);
      
      // 如果有头像，初始化文件列表
      if (user.avatar) {
        setFileList([{
          uid: '-1',
          name: 'avatar.png',
          status: 'done',
          url: user.avatar,
        }]);
      }
    }
  }, [user, form]);

  // 如果没有登录
  if (!user) {
    return (
      <div className="about-page">
        <div className="about-header">
          <Title level={2}>关于我</Title>
          <Text type="secondary">个人介绍</Text>
        </div>
        <div className="about-content">
          <Card className="login-prompt-card">
            <div className="login-prompt">
              <Title level={3} style={{ color: '#666', marginBottom: '20px' }}>
                <UserOutlined style={{ marginRight: '10px' }} />
                请先登录
              </Title>
              <Paragraph style={{ color: '#999', marginBottom: '30px' }}>
                登录后可以查看和编辑您的个人信息
              </Paragraph>
              <Button 
                type="primary" 
                size="large" 
                onClick={() => navigate('/auth')}
                style={{ width: '200px' }}
              >
                前往登录
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // 处理图片预览
  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  // 处理图片上传变化
  const handleChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  // 开始编辑
  const handleEdit = () => {
    setIsEditing(true);
  };

  // 取消编辑
  const handleCancel = () => {
    setIsEditing(false);
    form.resetFields();
    // 重置文件列表到原始状态
    if (user.avatar) {
      setFileList([{
        uid: '-1',
        name: 'avatar.png',
        status: 'done',
        url: user.avatar,
      }]);
    } else {
      setFileList([]);
    }
  };

  // 保存编辑
  const handleSave = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      
      let newAvatar = user.avatar;
      // 如果有新上传的图片
      if (fileList.length > 0 && fileList[0].originFileObj) {
        const file = fileList[0].originFileObj;
        newAvatar = await getBase64(file);
      }
      
      // 格式化数据
      const formattedValues = {
        bio: values.bio || '这个人很神秘，什么也没留下。',
        techStack: values.techStack ? 
          values.techStack.split(',').map(item => item.trim()).filter(item => item) : [],
        interests: values.interests ? 
          values.interests.split(',').map(item => item.trim()).filter(item => item) : [],
        github: values.github || '',
        twitter: values.twitter || '',
        website: values.website || '',
        avatar: newAvatar // 更新头像
      };
      
      console.log('保存用户数据:', formattedValues);
      
      if (onUpdateUser) {
        onUpdateUser(formattedValues);
      }
      setIsEditing(false);
      message.success('个人信息已更新！');
    } catch (error) {
      console.error('表单验证失败:', error);
      message.error('请检查表单填写是否正确');
    }
  };

  // 用户文章数据统计
  const userPosts = posts.filter(post => post.author === user.username);
  const totalPosts = userPosts.length;
  const totalViews = userPosts.reduce((sum, post) => sum + (post.views || 0), 0);
  const totalLikes = userPosts.reduce((sum, post) => sum + (post.likes || 0), 0);

  // 格式化显示数据
  const displayTechStack = Array.isArray(user.techStack) ? user.techStack : [];
  const displayInterests = Array.isArray(user.interests) ? user.interests : [];

  // 上传按钮
  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>上传</div>
    </div>
  );

  return (
    <div className="about-page">
      {/* 头部 */}
      <div className="about-header">
        <Title level={2}>关于我</Title>
        <Text type="secondary">个人介绍</Text>
      </div>

      <div className="about-content">
        {/* 个人信息卡片 */}
        <Card className="profile-card">
          <div className="profile-header">
            <div className="profile-avatar-section">
              {isEditing ? (
                <div className="avatar-upload-container">
                  <Upload
                    listType="picture-card"
                    fileList={fileList}
                    onPreview={handlePreview}
                    onChange={handleChange}
                    beforeUpload={() => false} // 阻止自动上传
                    accept="image/*"
                    maxCount={1}
                  >
                    {fileList.length >= 1 ? null : uploadButton}
                  </Upload>
                  <Modal open={previewOpen} footer={null} onCancel={() => setPreviewOpen(false)}>
                    <img alt="预览" style={{ width: '100%' }} src={previewImage} />
                  </Modal>
                </div>
              ) : (
                <>
                  <Avatar 
                    size={120} 
                    src={user.avatar} 
                    className="profile-avatar"
                    icon={<UserOutlined />}
                  />
                  <div className="avatar-overlay">
                    <Button 
                      type="link" 
                      icon={<EditOutlined />}
                      className="edit-avatar-btn"
                      onClick={() => setIsEditing(true)}
                    >
                      更换头像
                    </Button>
                  </div>
                </>
              )}
            </div>
            <div className="profile-info">
              <Title level={3} className="profile-username">
                {user.username}
              </Title>
              <div className="profile-email">
                <MailOutlined />
                <Text style={{ marginLeft: '8px' }}>{user.email}</Text>
              </div>
              <div className="profile-register-date">
                <CalendarOutlined />
                <Text style={{ marginLeft: '8px' }}>
                  注册时间：{user.registerDate || '2023-01-15'}
                </Text>
              </div>
            </div>
          </div>

          {/* 编辑按钮 */}
          {!isEditing ? (
            <div className="edit-button-section">
              <Button 
                type="primary" 
                icon={<EditOutlined />}
                onClick={handleEdit}
                className="edit-button"
              >
                编辑信息
              </Button>
            </div>
          ) : (
            <div className="edit-buttons">
              <Button 
                type="primary" 
                icon={<SaveOutlined />}
                onClick={handleSave}
                className="save-button"
              >
                保存
              </Button>
              <Button 
                icon={<CloseOutlined />}
                onClick={handleCancel}
                className="cancel-button"
              >
                取消
              </Button>
            </div>
          )}
        </Card>

        {/* 编辑表单 */}
        {isEditing ? (
          <Card className="edit-form-card">
            <Form form={form} layout="vertical">
              <Row gutter={[24, 16]}>
                <Col span={24}>
                  <Form.Item
                    name="bio"
                    label="个人简介"
                    rules={[{ max: 200, message: '个人简介不超过200字' }]}
                  >
                    <TextArea 
                      rows={4} 
                      placeholder="介绍一下你自己吧..." 
                      maxLength={200}
                      showCount
                    />
                  </Form.Item>
                </Col>
                
                <Col span={12}>
                  <Form.Item
                    name="techStack"
                    label="技术栈"
                    rules={[{ max: 100, message: '技术栈描述不超过100字' }]}
                  >
                    <AntInput 
                      placeholder="例如：React, Node.js, Python (用逗号分隔)"
                      prefix={<CodeOutlined />}
                    />
                  </Form.Item>
                </Col>
                
                <Col span={12}>
                  <Form.Item
                    name="interests"
                    label="兴趣爱好"
                    rules={[{ max: 100, message: '兴趣爱好描述不超过100字' }]}
                  >
                    <AntInput 
                      placeholder="例如：阅读, 音乐, 旅行 (用逗号分隔)"
                      prefix={<HeartOutlined />}
                    />
                  </Form.Item>
                </Col>
                
                <Col span={8}>
                  <Form.Item
                    name="github"
                    label="GitHub"
                    rules={[{ type: 'url', message: '请输入有效的URL地址' }]}
                  >
                    <AntInput 
                      placeholder="https://github.com/username"
                      prefix={<GithubOutlined />}
                    />
                  </Form.Item>
                </Col>
                
                <Col span={8}>
                  <Form.Item
                    name="twitter"
                    label="Twitter"
                    rules={[{ type: 'url', message: '请输入有效的URL地址' }]}
                  >
                    <AntInput 
                      placeholder="https://twitter.com/username"
                      prefix={<TwitterOutlined />}
                    />
                  </Form.Item>
                </Col>
                
                <Col span={8}>
                  <Form.Item
                    name="website"
                    label="个人网站"
                    rules={[{ type: 'url', message: '请输入有效的URL地址' }]}
                  >
                    <AntInput 
                      placeholder="https://example.com"
                      prefix={<LinkOutlined />}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        ) : (
          <Row gutter={[24, 24]} className="info-sections">
            {/* 个人简介 */}
            <Col span={24}>
              <Card title="个人简介" className="info-card">
                <Paragraph className="bio-text">
                  {user.bio || '这个人很神秘，什么也没留下。'}
                </Paragraph>
              </Card>
            </Col>

            {/* 技术栈 */}
            <Col span={12}>
              <Card title="技术栈" className="info-card">
                {displayTechStack && displayTechStack.length > 0 ? (
                  <div className="tags-container">
                    {displayTechStack.map((tech, index) => (
                      <span key={index} className="tech-tag">
                        {tech}
                      </span>
                    ))}
                  </div>
                ) : (
                  <Empty description="暂无技术栈信息" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </Card>
            </Col>

            {/* 兴趣爱好 */}
            <Col span={12}>
              <Card title="兴趣爱好" className="info-card">
                {displayInterests && displayInterests.length > 0 ? (
                  <div className="tags-container">
                    {displayInterests.map((interest, index) => (
                      <span key={index} className="interest-tag">
                        {interest}
                      </span>
                    ))}
                  </div>
                ) : (
                  <Empty description="暂无兴趣爱好" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </Card>
            </Col>

            {/* 社交媒体链接 */}
            {(user.github || user.twitter || user.website) && (
              <Col span={24}>
                <Card title="社交媒体" className="info-card">
                  <div className="social-links">
                    {user.github && (
                      <a 
                        href={user.github.startsWith('http') ? user.github : `https://${user.github}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="social-link"
                      >
                        <GithubOutlined /> GitHub
                      </a>
                    )}
                    {user.twitter && (
                      <a 
                        href={user.twitter.startsWith('http') ? user.twitter : `https://${user.twitter}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="social-link"
                      >
                        <TwitterOutlined /> Twitter
                      </a>
                    )}
                    {user.website && (
                      <a 
                        href={user.website.startsWith('http') ? user.website : `https://${user.website}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="social-link"
                      >
                        <LinkOutlined /> 个人网站
                      </a>
                    )}
                  </div>
                </Card>
              </Col>
            )}

            {/* 统计数据 */}
            <Col span={24}>
              <Card title="博客数据" className="stats-card">
                <Row gutter={[16, 16]}>
                  <Col span={6}>
                    <div className="stat-item">
                      <div className="stat-icon">
                        <FileTextOutlined />
                      </div>
                      <div className="stat-content">
                        <div className="stat-number">{totalPosts}</div>
                        <div className="stat-label">文章总数</div>
                      </div>
                    </div>
                  </Col>
                  <Col span={6}>
                    <div className="stat-item">
                      <div className="stat-icon">
                        <EyeOutlined />
                      </div>
                      <div className="stat-content">
                        <div className="stat-number">{totalViews}</div>
                        <div className="stat-label">总阅读量</div>
                      </div>
                    </div>
                  </Col>
                  <Col span={6}>
                    <div className="stat-item">
                      <div className="stat-icon">
                        <HeartOutlined />
                      </div>
                      <div className="stat-content">
                        <div className="stat-number">{totalLikes}</div>
                        <div className="stat-label">总点赞数</div>
                      </div>
                    </div>
                  </Col>
                  <Col span={6}>
                    <div className="stat-item">
                      <div className="stat-icon">
                        <CalendarOutlined />
                      </div>
                      <div className="stat-content">
                        <div className="stat-number">{user.registerDate || '2023-01-15'}</div>
                        <div className="stat-label">注册时间</div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
};

export default About;